import { captureHandledException } from '@/lib/sentry';
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { 
  MilestoneData, 
  BadgeData,
  getMilestones, 
  saveMilestone, 
  updateMilestone, 
  deleteMilestone,
  getBadges,
  getPrograms,
  deleteField
} from '@/lib/firebase';
import { useAuth } from '@/contexts/use-auth';
import { Target, Plus, Edit, Trash2, Filter, X } from 'lucide-react';

const TYPE_OPTIONS = ['program', 'streak', 'action'] as const;
const STREAK_TYPE_OPTIONS = ['dailyTask', 'steps', 'program'] as const;
const USER_ACTION_TYPE_OPTIONS = [
  'videoViewed',
  'seriesCompleted',
  'articleRead',
  'programStarted',
  'programCompleted',
  'stepTargetHitDaily',
  'stepTargetHitWeekly',
  'eventRegistered',
] as const;

const ACTION_TYPE_LABELS: Record<string, string> = {
  videoViewed: 'Video Viewed',
  seriesCompleted: 'Series Completed',
  articleRead: 'Article Read',
  programStarted: 'Program Started',
  programCompleted: 'Program Completed',
  stepTargetHitDaily: 'Daily Step Target Hit',
  stepTargetHitWeekly: 'Weekly Step Target Hit',
  eventRegistered: 'Event Registered',
};

export default function MilestonesPage() {
  const [milestones, setMilestones] = useState<MilestoneData[]>([]);
  const [badges, setBadges] = useState<BadgeData[]>([]);
  const [programs, setPrograms] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingMilestone, setEditingMilestone] = useState<MilestoneData | null>(null);
  const [formData, setFormData] = useState<Omit<MilestoneData, 'id'>>({
    type: 'program',
    enabled: true,
    value: 0,
    badgeId: '',
    programId: '',
    programName: '',
    programTaskNumber: '',
    streakType: undefined,
    eventType: ''
  });
  const [saving, setSaving] = useState(false);
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [secondaryFilter, setSecondaryFilter] = useState<string>('all');
  const { user } = useAuth();
  const { toast } = useToast();

  useEffect(() => {
    if (user) {
      loadData();
    }
  }, [user]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [milestonesData, badgesData, programsData] = await Promise.all([
        getMilestones(),
        getBadges(),
        getPrograms()
      ]);
      setMilestones(milestonesData);
      setBadges(badgesData);
      setPrograms(programsData);
    } catch (error: any) {
      captureHandledException(error, "milestones.load");
      toast({
        title: "Failed to load data",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddMilestone = () => {
    setEditingMilestone(null);
    setFormData({
      type: 'program',
      enabled: true,
      value: 0,
      badgeId: '',
      programId: '',
      programName: '',
      programTaskNumber: '',
      streakType: undefined,
      eventType: ''
    });
    setModalOpen(true);
  };

  const handleEditMilestone = (milestone: MilestoneData) => {
    setEditingMilestone(milestone);
    setFormData({
      type: milestone.type,
      enabled: milestone.enabled,
      value: milestone.value,
      badgeId: milestone.badgeId || '',
      programId: milestone.programId || '',
      programName: milestone.programName || '',
      programTaskNumber: milestone.programTaskNumber || '',
      streakType: milestone.streakType,
      eventType: milestone.appEventType || milestone.eventType || milestone.userActionType || ''
    });
    setModalOpen(true);
  };

  const handleSaveMilestone = async () => {
    // Validate based on type
    if (formData.type === 'program') {
      if (!formData.programId || !formData.programTaskNumber) {
        toast({
          title: "Validation error",
          description: "Please select a program and task number.",
          variant: "destructive",
        });
        return;
      }
    } else if (formData.type === 'streak') {
      if (!formData.streakType || formData.value <= 0) {
        toast({
          title: "Validation error",
          description: "Please select a streak type and enter a valid value.",
          variant: "destructive",
        });
        return;
      }
    } else if (formData.type === 'action') {
      if (!formData.eventType || formData.value <= 0) {
        toast({
          title: "Validation error",
          description: "Please select an action type and enter a valid value.",
          variant: "destructive",
        });
        return;
      }
    }

    try {
      setSaving(true);
      
      // Clean up data based on type - only include fields that have values
      const cleanedData: any = {
        type: formData.type,
        enabled: formData.enabled,
        value: formData.type === 'program' ? 0 : formData.value,
      };

      // Handle badge - add if present, or mark for deletion if editing and empty
      if (formData.badgeId && formData.badgeId.trim()) {
        cleanedData.badgeId = formData.badgeId;
      } else if (editingMilestone) {
        // Explicitly delete badgeId if editing and badge is empty
        cleanedData.badgeId = deleteField();
      }

      // Add type-specific fields and mark others for deletion
      if (formData.type === 'program') {
        cleanedData.programId = formData.programId;
        cleanedData.programName = formData.programName;
        cleanedData.programTaskNumber = formData.programTaskNumber;
        
        // Delete streak/action fields if editing
        if (editingMilestone) {
          cleanedData.streakType = deleteField();
          cleanedData.appEventType = deleteField();
          cleanedData.eventType = deleteField();
          cleanedData.userActionType = deleteField();
        }
      } else if (formData.type === 'streak') {
        cleanedData.streakType = formData.streakType;
        
        // Delete program/action fields if editing
        if (editingMilestone) {
          cleanedData.programId = deleteField();
          cleanedData.programName = deleteField();
          cleanedData.programTaskNumber = deleteField();
          cleanedData.appEventType = deleteField();
          cleanedData.eventType = deleteField();
          cleanedData.userActionType = deleteField();
        }
      } else if (formData.type === 'action') {
        cleanedData.appEventType = formData.eventType;
        
        // Delete program/streak fields if editing
        if (editingMilestone) {
          cleanedData.programId = deleteField();
          cleanedData.programName = deleteField();
          cleanedData.programTaskNumber = deleteField();
          cleanedData.streakType = deleteField();
          cleanedData.eventType = deleteField();
          cleanedData.userActionType = deleteField();
        }
      }
      
      if (editingMilestone) {
        await updateMilestone(editingMilestone.id!, cleanedData);
        toast({
          title: "Milestone updated",
          description: "Milestone has been updated successfully.",
        });
      } else {
        await saveMilestone(cleanedData);
        toast({
          title: "Milestone created",
          description: "Milestone has been added successfully.",
        });
      }
      
      setModalOpen(false);
      loadData();
    } catch (error: any) {
      captureHandledException(error, "milestones.save");
      toast({
        title: editingMilestone ? "Failed to update milestone" : "Failed to create milestone",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteMilestone = async (milestone: MilestoneData) => {
    if (!confirm(`Are you sure you want to delete this milestone? This action cannot be undone.`)) {
      return;
    }

    try {
      await deleteMilestone(milestone.id!);
      toast({
        title: "Milestone deleted",
        description: "Milestone has been deleted successfully.",
      });
      loadData();
    } catch (error: any) {
      captureHandledException(error, "milestones.delete");
      toast({
        title: "Failed to delete milestone",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleToggleEnabled = async (milestone: MilestoneData) => {
    try {
      await updateMilestone(milestone.id!, { enabled: !milestone.enabled });
      toast({
        title: "Milestone updated",
        description: `Milestone has been ${!milestone.enabled ? 'enabled' : 'disabled'}.`,
      });
      loadData();
    } catch (error: any) {
      captureHandledException(error, "milestones.update");
      toast({
        title: "Failed to update milestone",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleProgramSelect = (programId: string) => {
    const program = programs.find(p => p.id === programId);
    if (program) {
      setFormData(prev => ({
        ...prev,
        programId,
        programName: program.title || program.name
      }));
    }
  };

  const getSecondaryFilterOptions = () => {
    if (typeFilter === 'all') return [];
    
    if (typeFilter === 'program') {
      const programNames = Array.from(new Set(
        milestones
          .filter(m => m.type === 'program' && m.programName)
          .map(m => m.programName!)
      ));
      return programNames;
    } else if (typeFilter === 'streak') {
      const streakTypes = Array.from(new Set(
        milestones
          .filter(m => m.type === 'streak' && m.streakType)
          .map(m => m.streakType!)
      ));
      return streakTypes;
    } else if (typeFilter === 'action') {
      const actionTypes = Array.from(new Set(
        milestones
          .filter(m => m.type === 'action' && (m.appEventType || m.eventType || m.userActionType))
          .map(m => m.appEventType || m.eventType || m.userActionType!)
      ));
      return actionTypes;
    }
    return [];
  };

  const filteredMilestones = milestones
    .filter(milestone => {
      // Primary filter by type
      if (typeFilter !== 'all' && milestone.type !== typeFilter) {
        return false;
      }

      // Secondary filter
      if (secondaryFilter !== 'all') {
        if (typeFilter === 'program' && milestone.programName !== secondaryFilter) {
          return false;
        }
        if (typeFilter === 'streak' && milestone.streakType !== secondaryFilter) {
          return false;
        }
        if (typeFilter === 'action' && (milestone.appEventType || milestone.eventType || milestone.userActionType) !== secondaryFilter) {
          return false;
        }
      }

      return true;
    })
    .sort((a, b) => {
      // Sort based on milestone type
      if (a.type === 'program' && b.type === 'program') {
        // Sort by program name, then by task number
        const programCompare = (a.programName || '').localeCompare(b.programName || '');
        if (programCompare !== 0) return programCompare;
        return Number(a.programTaskNumber || 0) - Number(b.programTaskNumber || 0);
      } else if (a.type === 'streak' && b.type === 'streak') {
        // Sort by streak type, then by value
        const streakCompare = (a.streakType || '').localeCompare(b.streakType || '');
        if (streakCompare !== 0) return streakCompare;
        return (a.value || 0) - (b.value || 0);
      } else if (a.type === 'action' && b.type === 'action') {
        // Sort by action type, then by value
        const actionCompare = (a.appEventType || a.eventType || a.userActionType || '').localeCompare(b.appEventType || b.eventType || b.userActionType || '');
        if (actionCompare !== 0) return actionCompare;
        return (a.value || 0) - (b.value || 0);
      }
      
      // If different types, sort by type
      return a.type.localeCompare(b.type);
    });

  const getDisplayName = (milestone: MilestoneData) => {
    if (milestone.type === 'program') {
      return milestone.programName || 'Unknown Program';
    } else if (milestone.type === 'streak') {
      return milestone.streakType || 'Unknown Streak';
    } else if (milestone.type === 'action') {
      const et = milestone.appEventType || milestone.eventType || milestone.userActionType || '';
      return ACTION_TYPE_LABELS[et] || et || 'Unknown Action';
    }
    return '-';
  };

  const getDisplayValue = (milestone: MilestoneData) => {
    if (milestone.type === 'program') {
      return `Day ${milestone.programTaskNumber || '-'}`;
    }
    return milestone.value.toString();
  };

  const getBadgeName = (badgeId?: string) => {
    if (!badgeId) return '-';
    const badge = badges.find(b => b.id === badgeId);
    return badge?.name || '-';
  };

  const selectedProgram = programs.find(p => p.id === formData.programId);
  const maxDayNumber = (() => {
    if (!selectedProgram) return 0;
    const components: any[] = selectedProgram.components || [];
    if (components.length > 0) {
      let maxEnd = 0;
      for (const comp of components) {
        const start = comp.startOnDay || 1;
        const tasks: any[] = comp.tasks || [];
        const count = comp.componentType === 'steps' ? 1 : tasks.length;
        if (count > 0) maxEnd = Math.max(maxEnd, start + count - 1);
      }
      return maxEnd;
    }
    // Fallback for legacy programs with tasks array
    return selectedProgram.tasks?.length || 0;
  })();

  return (
    <div className="flex-1 p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center space-x-3">
          <Target className="w-8 h-8 text-primary" />
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Milestones</h1>
            <p className="text-slate-600">Manage achievement milestones</p>
          </div>
        </div>
        <Button onClick={handleAddMilestone} className="flex items-center space-x-2" data-testid="button-add-milestone">
          <Plus className="w-4 h-4" />
          <span>Add Milestone</span>
        </Button>
      </div>

      {/* Stats Card */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Target className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-slate-600">Total Milestones</p>
                <p className="text-2xl font-bold text-slate-900" data-testid="text-total-milestones">{milestones.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Target className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-slate-600">Enabled</p>
                <p className="text-2xl font-bold text-slate-900" data-testid="text-enabled-milestones">
                  {milestones.filter(m => m.enabled).length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="p-6">
          <div className="flex items-center space-x-4">
            <Filter className="w-5 h-5 text-slate-500" />
            <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Type Filter */}
              <div>
                <Label className="text-sm font-medium mb-2">Filter by Type</Label>
                <Select value={typeFilter} onValueChange={(value) => {
                  setTypeFilter(value);
                  setSecondaryFilter('all');
                }}>
                  <SelectTrigger data-testid="select-type-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="program">Program</SelectItem>
                    <SelectItem value="streak">Streak</SelectItem>
                    <SelectItem value="action">Action</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Secondary Filter */}
              {typeFilter !== 'all' && getSecondaryFilterOptions().length > 0 && (
                <div>
                  <Label className="text-sm font-medium mb-2">
                    Filter by {typeFilter === 'program' ? 'Program' : typeFilter === 'streak' ? 'Streak Type' : 'Action Type'}
                  </Label>
                  <Select value={secondaryFilter} onValueChange={setSecondaryFilter}>
                    <SelectTrigger data-testid="select-secondary-filter">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      {getSecondaryFilterOptions().map((option) => (
                        <SelectItem key={option} value={option}>
                          {option}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            
            {(typeFilter !== 'all' || secondaryFilter !== 'all') && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setTypeFilter('all');
                  setSecondaryFilter('all');
                }}
                data-testid="button-clear-filters"
              >
                <X className="w-4 h-4 mr-2" />
                Clear Filters
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Milestones List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-slate-600">Loading milestones...</p>
        </div>
      ) : filteredMilestones.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Target className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600 mb-4">
              {typeFilter !== 'all' || secondaryFilter !== 'all' 
                ? 'No milestones match the current filters' 
                : 'No milestones found'}
            </p>
            <Button onClick={handleAddMilestone} data-testid="button-add-first-milestone">
              <Plus className="w-4 h-4 mr-2" />
              Add Your First Milestone
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Name/Category</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Value/Task</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Badge</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Enabled</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-slate-200">
                  {filteredMilestones.map((milestone) => (
                    <tr key={milestone.id} className="hover:bg-slate-50" data-testid={`row-milestone-${milestone.id}`}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 capitalize" data-testid={`text-milestone-type-${milestone.id}`}>
                          {milestone.type}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="font-medium text-slate-900" data-testid={`text-milestone-name-${milestone.id}`}>
                          {getDisplayName(milestone)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span data-testid={`text-milestone-value-${milestone.id}`}>{getDisplayValue(milestone)}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-slate-600" data-testid={`text-milestone-badge-${milestone.id}`}>
                          {getBadgeName(milestone.badgeId)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Switch
                          checked={milestone.enabled}
                          onCheckedChange={() => handleToggleEnabled(milestone)}
                          data-testid={`switch-milestone-enabled-${milestone.id}`}
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditMilestone(milestone)}
                          className="mr-2"
                          data-testid={`button-edit-milestone-${milestone.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteMilestone(milestone)}
                          className="text-red-600 hover:text-red-700"
                          data-testid={`button-delete-milestone-${milestone.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Add/Edit Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingMilestone ? 'Edit Milestone' : 'Add New Milestone'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            {/* Type */}
            <div>
              <Label htmlFor="type">Milestone Type *</Label>
              <Select
                value={formData.type}
                onValueChange={(value) => setFormData(prev => ({ 
                  ...prev, 
                  type: value as MilestoneData['type'],
                  // Reset type-specific fields
                  programId: '',
                  programName: '',
                  programTaskNumber: '',
                  streakType: undefined,
                  eventType: '',
                  value: value === 'program' ? 0 : prev.value
                }))}
              >
                <SelectTrigger data-testid="select-milestone-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TYPE_OPTIONS.map((type) => (
                    <SelectItem key={type} value={type}>
                      <span className="capitalize">{type}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Program Type Fields */}
            {formData.type === 'program' && (
              <>
                <div>
                  <Label htmlFor="program">Program *</Label>
                  <Select
                    value={formData.programId}
                    onValueChange={handleProgramSelect}
                  >
                    <SelectTrigger data-testid="select-program">
                      <SelectValue placeholder="Select a program" />
                    </SelectTrigger>
                    <SelectContent>
                      {programs.map((program) => (
                        <SelectItem key={program.id} value={program.id!}>
                          {program.title || program.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="taskNumber">Day Number *</Label>
                  <Select
                    value={formData.programTaskNumber}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, programTaskNumber: value }))}
                    disabled={!formData.programId}
                  >
                    <SelectTrigger data-testid="select-task-number">
                      <SelectValue placeholder="Select day number" />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: maxDayNumber }, (_, i) => i + 1).map((num) => (
                        <SelectItem key={num} value={num.toString()}>
                          Day {num}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {formData.programId && maxDayNumber === 0 && (
                    <p className="text-sm text-amber-600 mt-1">This program has no days configured</p>
                  )}
                </div>
              </>
            )}

            {/* Streak Type Fields */}
            {formData.type === 'streak' && (
              <>
                <div>
                  <Label htmlFor="streakType">Streak Type *</Label>
                  <Select
                    value={formData.streakType}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, streakType: value as MilestoneData['streakType'] }))}
                  >
                    <SelectTrigger data-testid="select-streak-type">
                      <SelectValue placeholder="Select streak type" />
                    </SelectTrigger>
                    <SelectContent>
                      {STREAK_TYPE_OPTIONS.map((type) => (
                        <SelectItem key={type} value={type}>
                          <span className="capitalize">{type.replace(/([A-Z])/g, ' $1').trim()}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="value">Value (Days/Count) *</Label>
                  <Input
                    id="value"
                    type="number"
                    min="1"
                    value={formData.value}
                    onChange={(e) => setFormData(prev => ({ ...prev, value: parseInt(e.target.value) || 0 }))}
                    placeholder="Enter value"
                    data-testid="input-streak-value"
                  />
                </div>
              </>
            )}

            {/* Action Type Fields */}
            {formData.type === 'action' && (
              <>
                <div>
                  <Label htmlFor="eventType">Action Type *</Label>
                  <Select
                    value={formData.eventType}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, eventType: value }))}
                  >
                    <SelectTrigger data-testid="select-action-type">
                      <SelectValue placeholder="Select action type" />
                    </SelectTrigger>
                    <SelectContent>
                      {USER_ACTION_TYPE_OPTIONS.map((type) => (
                        <SelectItem key={type} value={type}>
                          {ACTION_TYPE_LABELS[type]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="value">Value (Count) *</Label>
                  <Input
                    id="value"
                    type="number"
                    min="1"
                    value={formData.value}
                    onChange={(e) => setFormData(prev => ({ ...prev, value: parseInt(e.target.value) || 0 }))}
                    placeholder="Enter value"
                    data-testid="input-action-value"
                  />
                </div>
              </>
            )}

            {/* Badge (Optional for all types) */}
            <div>
              <Label htmlFor="badge">Badge (optional)</Label>
              <Select
                value={formData.badgeId || "none"}
                onValueChange={(value) => setFormData(prev => ({ ...prev, badgeId: value === "none" ? "" : value }))}
              >
                <SelectTrigger data-testid="select-badge">
                  <SelectValue placeholder="Select a badge" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Badge</SelectItem>
                  {badges
                    .filter(badge =>
                      !milestones.some(m => m.badgeId === badge.id) ||
                      badge.id === editingMilestone?.badgeId
                    )
                    .map((badge) => (
                      <SelectItem key={badge.id} value={badge.id!}>
                        {badge.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            {/* Enabled Toggle */}
            <div className="flex items-center space-x-2">
              <Switch
                id="enabled"
                checked={formData.enabled}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, enabled: checked }))}
                data-testid="switch-milestone-enabled-form"
              />
              <Label htmlFor="enabled" className="cursor-pointer">Enabled</Label>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-2 pt-4">
              <Button
                variant="outline"
                onClick={() => setModalOpen(false)}
                disabled={saving}
                data-testid="button-cancel-milestone"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveMilestone}
                disabled={saving}
                data-testid="button-save-milestone"
              >
                {saving ? 'Saving...' : (editingMilestone ? 'Update Milestone' : 'Create Milestone')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
